// src/app/layout/MobileNavDrawer.tsx
import { useEffect } from 'react';
import { X } from 'lucide-react';
import { NavList } from './NavList';
import { SidebarUserSection } from './SidebarUserSection';

// Mobile/tablet (< lg) counterpart to Sidebar. Renders the exact same
// NavList + SidebarUserSection that the desktop Sidebar uses — no
// second copy of the nav data or the logout flow, just a different
// container (slide-in drawer instead of a fixed column).

interface MobileNavDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileNavDrawer({ isOpen, onClose }: MobileNavDrawerProps) {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  return (
    <div className="lg:hidden" aria-hidden={!isOpen}>
      {/* Overlay — click to close */}
      <div
        onClick={onClose}
        className={[
          'fixed inset-0 z-40 bg-black/50 transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        ].join(' ')}
      />

      {/* Drawer panel — slides in from the RTL start side (right) */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="منوی ناوبری"
        className={[
          'fixed inset-y-0 start-0 z-50 flex w-72 max-w-[80vw] flex-col',
          'bg-[var(--vania-colors-background-primary,#0b0f1a)]',
          'border-e border-[var(--vania-colors-border-subtle,rgba(255,255,255,0.08))]',
          'transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        ].join(' ')}
      >
        {/* Brand area */}
        <div className="flex items-center justify-between gap-2.5 border-b border-[var(--vania-colors-border-subtle,rgba(255,255,255,0.08))] px-4 py-4">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--vania-accent-violet,#7c6cff)] text-sm font-bold text-white">
              V
            </span>
            <span className="min-w-0 truncate text-sm font-semibold text-[var(--vania-colors-text-primary,#f5f6fa)]">
              VaniaLabs
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="بستن منو"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[var(--vania-colors-text-secondary,#a3aab8)] transition-colors hover:bg-[var(--vania-colors-surface-secondary,rgba(255,255,255,0.06))]"
          >
            <X size={18} strokeWidth={2.2} />
          </button>
        </div>

        <NavList onItemSelect={onClose} />

        <SidebarUserSection onAfterLogout={onClose} />
      </aside>
    </div>
  );
}
